import React, { memo, useMemo } from 'react';
import isEqual from 'lodash.isequal';
import { Wrapper } from '@googlemaps/react-wrapper';

import { ContainerMap, styleMap } from './styles';
import { Map, Path, Marker } from './_internal';
import { useLanguage } from '../../../hooks/useLanguage';
import {
  MAP_LIBRARIES,
  MAP_REGION,
  mapsLanguage,
} from './mapsLoaderOptions';

// ═══════════════════════════════════════════════════════════════════
// MultiPathMap — renders N polylines + M markers on a single Google Map.
// Reuses the canonical Wrapper/Map/Path/Marker primitives from
// ./_internal so that MapComponent's public API stays byte-identical.
// ═══════════════════════════════════════════════════════════════════

export interface LatLng {
  lat: number;
  lng: number;
}

export interface MultiPathEntry {
  id: string;
  coordinates: LatLng[];
  color: string;
  strokeWeight?: number;
  strokeOpacity?: number;
  /**
   * When true, renders the polyline as a dashed line via Google Maps
   * iconSequence (invisible line + circular dot icons repeated along).
   */
  dashed?: boolean;
  /**
   * When true, overlays repeated forward-arrow icons along the polyline
   * (Google Maps `icons` iconSequence pattern) to show direction of travel.
   * Combines with `dashed` — both icon sequences render on the same line.
   * Used by MapWithSegments (SR) for the active GPS track, and (2026-07-08)
   * for the SELECTED tram — reuses the same mechanism so a selected tram
   * visually shows its sentido (ascendente/descendente). The polyline's
   * `coordinates` order must already follow real travel direction (icons
   * auto-rotate to the line's own direction, −y = forward) — see
   * MapWithSegments `loadSegmentCoordinates` for the PK-based reversal that
   * guarantees this for descending trams.
   */
  directionArrows?: boolean;
  /**
   * Color de las flechas de `directionArrows` — por defecto el `color` de
   * la propia línea. Bug primer-click 2026-07-08: para el tram SELECCIONADO
   * las flechas iban del MISMO color que su línea sólida (la flecha a
   * scale 3 mide ~9.6px de ancho, la línea seleccionada 10px) →
   * invisibles, mismo color sobre mismo color. En la traza GPS nunca se
   * notó porque su stroke principal es transparente+dashed y las flechas
   * caen en los huecos. El consumidor pasa aquí un color de CONTRASTE
   * (blanco) para líneas sólidas — patrón "chevrons blancos sobre línea de
   * ruta" de Google Maps directions.
   */
  directionArrowColor?: string;
  /**
   * Table→map tram selection (2026-07-08): when true, this entry paints
   * above every non-emphasized entry (still below markers/dots — see
   * `Z_INDEX_*` below) and swaps its casing from the default white outline
   * to `casingColor` (falls back to the entry's own `color` when omitted)
   * for a stronger, darker silhouette. Non-emphasized entries are expected
   * to arrive with a lower `strokeOpacity` from the caller (dimming is the
   * caller's responsibility — this flag only controls stacking + casing).
   * Used by MapWithSegments (SR).
   */
  emphasized?: boolean;
  /**
   * Casing stroke color override — only applies when `emphasized` is true.
   * Defaults to the entry's own `color` when omitted.
   */
  casingColor?: string;
  /**
   * Selección clicando el PROPIO trazado (2026-07-08 follow-up): cuando se
   * proporciona (y la entry no es `dashed`), se renderiza una hit-polyline
   * INVISIBLE y más ancha (ver `HIT_AREA_*`) encima de la línea visible +
   * casing, y el click/tap sobre ella dispara este handler. La línea
   * visible y la casing van con `clickable: false` — toda la interacción
   * pasa por la hit line, que además aporta el cursor pointer en hover
   * (comportamiento por defecto de Google Maps sobre overlays clicables).
   * Entries sin `onClick` (traza GPS, route-fallback) quedan
   * `clickable: false` en todas sus polylines — no interactivas.
   */
  onClick?: () => void;
}

export interface MultiPathMarker {
  id: string;
  position: LatLng;
  icon?: google.maps.Icon | google.maps.Symbol;
  /**
   * Bearing in degrees (0 = north, clockwise). When the marker is
   * `cursorIcon`, this rotates the whole navigation-arrow icon to the
   * bearing (see `_internal.createCursorArrowContent`). Used by
   * MapWithSegments (SR) for the GPS cursor.
   */
  rotationDeg?: number;
  /**
   * When true (together with `icon` as a Symbol), renders the marker as
   * the GPS cursor (a navigation-arrow icon rotated to `rotationDeg`, or
   * a plain filled dot when no bearing is known yet) instead of the
   * default colored circle. Explicit discriminator, independent of
   * `rotationDeg` — the cursor still renders with a single GPS point (as
   * a dot, no arrow yet). Used by MapWithSegments (SR).
   */
  cursorIcon?: boolean;
  /**
   * When true (together with `icon` as a Symbol), renders the marker as the
   * "direction change" glyph (circle + inline ↺ icon) instead of the
   * default colored circle. Used by MapWithSegments (SR) at the point where
   * two consecutive closed trams switch `direction`.
   */
  directionChangeIcon?: boolean;
  /**
   * The group gathers trams with DIFFERENT causes, so no single color can be
   * asserted: the dot is painted neutral and the causes become readable as you
   * zoom in. Used by MapWithSegments (SR).
   */
  neutralFill?: boolean;
  /** Number of trams converging here; >1 paints the counter badge. */
  badgeCount?: number;
  /** This dot currently stands for the SELECTED tram of its group. */
  selectedOutline?: boolean;
  /** Color of the selection ring and of the counter badge. */
  accentColor?: string;
  /**
   * Click handler — forwarded to `_internal.Marker` (`AdvancedMarkerElement`
   * click listener). Used by MapWithSegments (SR) pause markers to call
   * `onSegmentClick(segment)`.
   */
  onClick?: () => void;
}

export interface MultiPathMapProps {
  apiKey: string;
  /**
   * Fires on every map `idle` (after pan or zoom) with the current zoom level.
   * MapWithSegments (SR) uses it to group markers by SCREEN distance: the
   * threshold in meters depends on the zoom, so without this the grouping
   * could only ever be zoom-independent — and at route zoom levels the dots
   * pile onto each other. The consumer must pass a stable callback and bail
   * when the zoom did not actually change (`idle` also fires on pan).
   */
  onViewportChange?: (zoom: number) => void;
  /**
   * Tap/click on the map background (not on a marker or a polyline). Touch has
   * no "press Escape": without this there is no way out of an expanded marker
   * fan other than committing to one of its markers. Used by MapWithSegments
   * (SR) to collapse it.
   */
  onMapClick?: () => void;
  zoom?: number;
  center: LatLng;
  paths: MultiPathEntry[];
  markers?: MultiPathMarker[];
  /**
   * When provided (>=2 points), the map fits its viewport to these
   * coordinates instead of using `center`/`zoom` (via `fitBounds`). Pass
   * ONLY the coordinates that should shape the viewport — e.g. MapWithSegments
   * (SR) passes the segment polylines' coordinates and deliberately excludes
   * the GPS track, so GPS outliers never expand the encuadre.
   */
  fitBoundsCoordinates?: LatLng[];
  /**
   * Botón de recentrar (2026-07-08): el efecto de fitBounds lleva un bail
   * de igualdad (mismas coordenadas → no re-encuadra, para no pelear con
   * el pan/zoom manual). Este nonce entra en esa clave de igualdad —
   * incrementarlo fuerza un re-encuadre aunque las coordenadas sean
   * idénticas (re-click del botón). Ver `_internal.Map`.
   */
  fitBoundsNonce?: number;
  /** Padding (px) del fitBounds — default 40 (encuadre inicial). */
  fitBoundsPadding?: number;
}

const DASH_SYMBOL: google.maps.Symbol = {
  // Circle used as dash dot; we scale it small for a dotted-dashed look.
  path: 'M 0,-1 0,1',
  strokeOpacity: 1,
  scale: 3,
};

// ═══════════════════════════════════════════════════════════════════
// CASING — industry-standard "directions style" line treatment (Google
// Maps/Mapbox route rendering): a slightly wider white outline drawn
// BELOW each solid segment line so it reads crisply over any basemap
// (satellite, terrain, other roads) instead of blending into it. Only
// solid segment lines get a casing — the dashed GPS track stays
// visually secondary (no casing, lowest zIndex) per design decision.
// ═══════════════════════════════════════════════════════════════════
const CASING_COLOR = '#FFFFFF';
const CASING_STROKE_OPACITY = 0.95;
const CASING_WIDTH_EXTRA_PX = 3;

// Explicit stacking order (google.maps.PolylineOptions.zIndex) — higher
// paints on top. Keeps the dashed GPS trace under every segment line,
// casings under their own main line, regardless of paths[] array order.
// `EMPHASIZED_*` (2026-07-08, table→map selection) sit above every regular
// segment + its casing, so the selected tram is never occluded by a
// neighboring non-selected tram. Markers (dots/cursor) are a separate
// AdvancedMarkerElement overlay pane, painted above all polylines
// regardless of these values — no zIndex needed there.
const Z_INDEX_GPS_TRACK = 1;
const Z_INDEX_CASING = 2;
const Z_INDEX_SEGMENT = 3;
const Z_INDEX_EMPHASIZED_CASING = 4;
const Z_INDEX_EMPHASIZED_SEGMENT = 5;
const Z_INDEX_HIT_AREA = 6;

// Hit-polyline invisible por tramo clicable (2026-07-08 follow-up): las
// líneas de tramo son finas (7-10px) — un target táctil digno pide ~16px.
// Google Maps hace el hit-testing de una polyline sobre su stroke, así que
// la técnica estándar es una segunda polyline más ancha y (casi) invisible
// SOLO para capturar clicks/taps. `strokeOpacity: 0.001` en vez de `0`
// exacto: una polyline totalmente transparente puede quedar fuera del
// hit-testing según la versión del renderer (workaround documentado por la
// comunidad Maps JS); a 0.001 es imperceptible pero clicable. Su zIndex va
// por encima de TODAS las polylines (nunca bloquea los dots de pausa/cursor
// GPS: los markers son AdvancedMarkerElement, un pane DOM superior con su
// propio hit-testing, ver comentario de Z_INDEX arriba).
const HIT_AREA_STROKE_WEIGHT = 16;
const HIT_AREA_STROKE_OPACITY = 0.001;

// Forward arrow overlaid on direction-arrow-enabled polylines. Scale/repeat
// tuned to read at route zoom levels (~13-15) without cluttering the line.
// ⚠️ SVG path LITERAL (equivalente visual a FORWARD_CLOSED_ARROW) — NUNCA
// `google.maps.SymbolPath.*`: los children JSX de <Wrapper> se evalúan
// EAGERLY al renderizar MultiPathMapInner (React construye los elements
// antes de que Wrapper decida montarlos), es decir ANTES de que cargue el
// script de Maps. Tocar el global `google` ahí lanzaba `ReferenceError:
// google is not defined` y tumbaba el componente (cazado en navegador real).
// Los symbols de una polyline se auto-rotan al sentido de la línea
// (−y apunta hacia adelante), así que la flecha marca la marcha sin JS extra.
const DIRECTION_ARROW_PATH = 'M 0,-2 L -1.6,1.6 L 1.6,1.6 Z';
const DIRECTION_ARROW_SCALE = 3;
const DIRECTION_ARROW_REPEAT = '70px';
const DIRECTION_ARROW_OFFSET = '20px';

const MultiPathMapInner = ({
  apiKey,
  zoom = 14,
  center,
  paths,
  markers = [],
  fitBoundsCoordinates,
  fitBoundsNonce,
  fitBoundsPadding,
  onViewportChange,
  onMapClick,
}: MultiPathMapProps) => {
  const { currentLanguage } = useLanguage();

  // `onIdle` entrega el mapa; aquí solo interesa el zoom. Se envuelve para no
  // filtrar la instancia de Google Maps a los consumidores.
  const handleIdle = useMemo(
    () =>
      onViewportChange
        ? (map: google.maps.Map) => {
            const current = map.getZoom();
            if (typeof current === 'number') onViewportChange(current);
          }
        : undefined,
    [onViewportChange]
  );

  return (
    <ContainerMap>
      <Wrapper
        apiKey={apiKey}
        language={mapsLanguage(currentLanguage)}
        region={MAP_REGION}
        libraries={MAP_LIBRARIES}
      >
        <Map
          center={center}
          zoom={zoom}
          fitBoundsCoordinates={fitBoundsCoordinates}
          fitBoundsNonce={fitBoundsNonce}
          fitBoundsPadding={fitBoundsPadding}
          onIdle={handleIdle}
          onClick={onMapClick}
          style={styleMap}
          clickableIcons={false}
          disableDefaultUI={true}
        >
          {paths.flatMap((entry) => {
            const mainStrokeWeight = entry.strokeWeight ?? 6;
            const baseOptions: google.maps.PolylineOptions = {
              path: entry.coordinates,
              geodesic: true,
              strokeColor: entry.color,
              strokeOpacity: entry.dashed ? 0 : entry.strokeOpacity ?? 1,
              strokeWeight: mainStrokeWeight,
              // Toda la interacción de click va por la hit-polyline (abajo)
              // — la línea visible nunca captura pointer events (evita
              // hit-targets duplicados y deja pasar el click del mapa
              // cuando la entry no es clicable).
              clickable: false,
              zIndex: entry.dashed
                ? Z_INDEX_GPS_TRACK
                : entry.emphasized
                  ? Z_INDEX_EMPHASIZED_SEGMENT
                  : Z_INDEX_SEGMENT,
            };

            const icons: google.maps.IconSequence[] = [];

            if (entry.dashed) {
              icons.push({
                icon: { ...DASH_SYMBOL, strokeColor: entry.color },
                offset: '0',
                repeat: '14px',
              });
            }

            if (entry.directionArrows) {
              // Path SVG literal (ver DIRECTION_ARROW_PATH): cero acceso al
              // global `google` en render — este .map() se ejecuta antes de
              // que el script de Maps esté cargado.
              const arrowColor = entry.directionArrowColor ?? entry.color;
              icons.push({
                icon: {
                  path: DIRECTION_ARROW_PATH,
                  scale: DIRECTION_ARROW_SCALE,
                  strokeColor: arrowColor,
                  fillColor: arrowColor,
                  fillOpacity: 1,
                  strokeOpacity: 1,
                  strokeWeight: 1,
                },
                offset: DIRECTION_ARROW_OFFSET,
                repeat: DIRECTION_ARROW_REPEAT,
              });
            }

            // SIEMPRE asignar `icons` — aunque esté vacío. Bug primer-click
            // 2026-07-08: `Polyline.setOptions` MERGEA opciones; si la key
            // `icons` se omite cuando no hay iconos, una polyline que TUVO
            // flechas (tram previamente seleccionado) las conserva para
            // siempre tras deseleccionar — las flechas "fantasma" a
            // fillOpacity 1 sobre la línea atenuada (0.45) eran lo que el
            // usuario veía "a partir del segundo click". El array vacío las
            // limpia explícitamente en cada cambio de selección.
            baseOptions.icons = icons;

            const elements: JSX.Element[] = [];

            // Casing — solo líneas sólidas de tramo (nunca la traza GPS
            // discontinua). Mismas coordenadas, blanco, algo más ancha,
            // zIndex por debajo de la línea principal: efecto "outline"
            // estándar de mapas de rutas (Google/Mapbox directions).
            if (!entry.dashed) {
              elements.push(
                <Path
                  key={`${entry.id}-casing`}
                  path={entry.coordinates}
                  geodesic
                  strokeColor={
                    entry.emphasized
                      ? entry.casingColor ?? entry.color
                      : CASING_COLOR
                  }
                  strokeOpacity={
                    entry.emphasized ? 1 : CASING_STROKE_OPACITY
                  }
                  strokeWeight={mainStrokeWeight + CASING_WIDTH_EXTRA_PX}
                  clickable={false}
                  zIndex={
                    entry.emphasized
                      ? Z_INDEX_EMPHASIZED_CASING
                      : Z_INDEX_CASING
                  }
                />
              );
            }

            elements.push(<Path key={entry.id} {...baseOptions} />);

            // Hit-polyline invisible (ver HIT_AREA_*) — solo para entries
            // clicables y sólidas (nunca la traza GPS discontinua). Va
            // ENCIMA de todas las polylines para que el tap gane siempre,
            // pero por debajo del pane de markers (dots de pausa intactos).
            if (entry.onClick && !entry.dashed) {
              elements.push(
                <Path
                  key={`${entry.id}-hit`}
                  path={entry.coordinates}
                  geodesic
                  strokeColor={entry.color}
                  strokeOpacity={HIT_AREA_STROKE_OPACITY}
                  strokeWeight={HIT_AREA_STROKE_WEIGHT}
                  clickable
                  zIndex={Z_INDEX_HIT_AREA}
                  onClick={entry.onClick}
                />
              );
            }
            return elements;
          })}

          {markers.map((m) => (
            <Marker
              key={m.id}
              position={m.position}
              rotationDeg={m.rotationDeg}
              cursorIcon={m.cursorIcon}
              directionChangeIcon={m.directionChangeIcon}
              neutralFill={m.neutralFill}
              badgeCount={m.badgeCount}
              selectedOutline={m.selectedOutline}
              accentColor={m.accentColor}
              onClick={m.onClick}
              // Los consumidores actuales (MapWithSegments SR) siempre
              // pasan `google.maps.Symbol`. El type de `MultiPathMarker`
              // acepta `Icon | Symbol | undefined` por compatibilidad
              // histórica; solo tratamos Symbol en la conversión a DOM.
              symbolIcon={
                m.icon && 'path' in m.icon
                  ? (m.icon as google.maps.Symbol)
                  : undefined
              }
            />
          ))}
        </Map>
      </Wrapper>
    </ContainerMap>
  );
};

// Deep-equal memo (same pattern the consumers of MapComponent use) so that
// identical paths arrays don't re-render and re-fire Google Maps options.
export const MultiPathMap = memo(MultiPathMapInner, isEqual);

export default MultiPathMap;
